/**
 * Motor de sincronização (plano, seção 19.2 e docs/decisoes/0002-sincronizacao.md).
 *
 * Ciclo: envia a fila (push) em lotes, depois baixa as mudanças (pull) até acabar.
 * Só conhece LocalDb (que conhece Storage) e uma API mínima: funciona igual na web,
 * no service worker e no app nativo.
 */
import { ApiError, type ChangeResult, type PullResponse, type PushResponse } from '../api/client';
import type { LocalDb } from '../db/local-db';
import { uuid } from '../domain/ids';
import { type EntityType, entityTypeSchema } from '../schemas/entities';
import type { OutboxEntry, SyncErrorEntry } from '../storage/storage';

export interface SyncApi {
  push(changes: OutboxEntry[], deviceId?: string): Promise<PushResponse>;
  pull(since: string, limit?: number): Promise<PullResponse>;
}

export type SyncState = 'idle' | 'syncing' | 'offline' | 'error' | 'unauthorized' | 'disabled';

export interface SyncStatus {
  state: SyncState;
  pending: number;
  lastSyncAt: string | null;
  error: string | null;
  /** alterações recusadas pelo servidor (ver Ajustes → Conta) */
  rejected: number;
}

export interface SyncEngineOptions {
  /** desligado no modo demonstração ou sem sessão */
  enabled: () => boolean;
  batchSize?: number;
  pullLimit?: number;
  onUnauthorized?: () => void;
  /** chamado com as entidades que mudaram vindas do servidor */
  onRemoteChanges?: (entities: Set<EntityType>, counts: { appointmentsFromLink: number }) => void;
}

export const META_CURSOR = 'sync_cursor';
export const META_DEVICE = 'device_id';
export const META_LAST_SYNC = 'last_sync_at';
export const META_ERRORS = 'sync_errors';

export class SyncEngine {
  private status: SyncStatus = { state: 'idle', pending: 0, lastSyncAt: null, error: null, rejected: 0 };
  private listeners = new Set<(s: SyncStatus) => void>();
  private running: Promise<SyncStatus> | null = null;
  private again = false;

  constructor(
    private readonly db: LocalDb,
    private readonly api: SyncApi,
    private readonly opts: SyncEngineOptions,
  ) {
    db.subscribe(() => {
      void this.refreshPending();
    });
  }

  getStatus(): SyncStatus {
    return this.status;
  }

  subscribe(listener: (s: SyncStatus) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private set(patch: Partial<SyncStatus>): void {
    this.status = { ...this.status, ...patch };
    for (const l of this.listeners) l(this.status);
  }

  async init(): Promise<void> {
    const [last, errors] = await Promise.all([
      this.db.storage.getMeta<string>(META_LAST_SYNC),
      this.db.storage.getMeta<SyncErrorEntry[]>(META_ERRORS),
    ]);
    this.set({
      lastSyncAt: last ?? null,
      rejected: errors?.length ?? 0,
      state: this.opts.enabled() ? this.status.state : 'disabled',
    });
    await this.refreshPending();
  }

  async refreshPending(): Promise<void> {
    const pending = await this.db.storage.countOutbox();
    if (pending !== this.status.pending) this.set({ pending });
  }

  async deviceId(): Promise<string> {
    let id = await this.db.storage.getMeta<string>(META_DEVICE);
    if (!id) {
      id = uuid();
      await this.db.storage.setMeta(META_DEVICE, id);
    }
    return id;
  }

  /** Roda um ciclo. Chamadas durante um ciclo agendam mais um no fim. */
  sync(): Promise<SyncStatus> {
    if (this.running) {
      this.again = true;
      return this.running;
    }
    this.running = (async () => {
      let result: SyncStatus;
      do {
        this.again = false;
        result = await this.cycle();
      } while (this.again && result.state === 'idle');
      return result;
    })().finally(() => {
      this.running = null;
    });
    return this.running;
  }

  private async cycle(): Promise<SyncStatus> {
    if (!this.opts.enabled()) {
      this.set({ state: 'disabled' });
      return this.status;
    }
    this.set({ state: 'syncing', error: null });
    try {
      await this.pushAll();
      await this.pullAll();
      const now = this.db.nowIso();
      await this.db.storage.setMeta(META_LAST_SYNC, now);
      await this.refreshPending();
      this.set({ state: 'idle', lastSyncAt: now });
    } catch (e) {
      await this.refreshPending();
      if (e instanceof ApiError && e.status === 401) {
        this.set({ state: 'unauthorized', error: e.message });
        this.opts.onUnauthorized?.();
      } else if (e instanceof ApiError && e.isNetwork) {
        this.set({ state: 'offline', error: null });
      } else {
        this.set({ state: 'error', error: e instanceof Error ? e.message : String(e) });
      }
    }
    return this.status;
  }

  private async pushAll(): Promise<void> {
    const batchSize = this.opts.batchSize ?? 100;
    const deviceId = await this.deviceId();
    for (let round = 0; round < 100; round++) {
      const entries = await this.db.storage.peekOutbox(batchSize);
      if (entries.length === 0) return;
      const response = await this.api.push(entries, deviceId);
      await this.applyPushResults(entries, response.results ?? []);
      // Proteção: se o servidor não respondeu nenhum item, não fica em loop.
      if ((response.results ?? []).length === 0) return;
    }
  }

  private async applyPushResults(entries: OutboxEntry[], results: ChangeResult[]): Promise<void> {
    const byOp = new Map(results.map((r) => [r.op_id, r]));
    const rejected: SyncErrorEntry[] = [];
    await this.db.batch(async () => {
      const done: string[] = [];
      for (const entry of entries) {
        const r = byOp.get(entry.op_id);
        if (!r) continue;
        done.push(entry.op_id);
        if (r.status === 'rejected') {
          rejected.push({
            op_id: entry.op_id,
            entity: entry.entity,
            id: entry.id,
            reason: r.reason ?? null,
            errors: (r.errors as Record<string, string[]> | null | undefined) ?? null,
            at: this.db.nowIso(),
          });
        }
      }
      await this.db.storage.removeOutbox(done);
      for (const entry of entries) {
        const r = byOp.get(entry.op_id);
        if (r?.record) await this.db.applyRemote(entry.entity, r.record);
      }
      if (rejected.length > 0) {
        const previous = (await this.db.storage.getMeta<SyncErrorEntry[]>(META_ERRORS)) ?? [];
        const all = [...previous, ...rejected].slice(-50);
        await this.db.storage.setMeta(META_ERRORS, all);
        this.set({ rejected: all.length });
      }
    });
  }

  private async pullAll(): Promise<void> {
    let cursor = (await this.db.storage.getMeta<string>(META_CURSOR)) ?? '0';
    const changed = new Set<EntityType>();
    let appointmentsFromLink = 0;
    for (let round = 0; round < 200; round++) {
      const res = await this.api.pull(cursor, this.opts.pullLimit ?? 500);
      await this.db.batch(async () => {
        for (const change of res.changes) {
          const entity = entityTypeSchema.safeParse(change.entity);
          if (!entity.success) continue;
          const before = entity.data === 'appointment' ? await this.db.getRaw('appointment', String(change.record.id)) : undefined;
          const saved = await this.db.applyRemote(entity.data, change.record);
          if (saved) {
            changed.add(entity.data);
            if (entity.data === 'appointment' && !before && change.record.source === 'link') appointmentsFromLink++;
          }
        }
        cursor = res.cursor || cursor;
        await this.db.storage.setMeta(META_CURSOR, cursor);
      });
      if (!res.has_more) break;
    }
    if (changed.size > 0) this.opts.onRemoteChanges?.(changed, { appointmentsFromLink });
  }

  async syncErrors(): Promise<SyncErrorEntry[]> {
    return (await this.db.storage.getMeta<SyncErrorEntry[]>(META_ERRORS)) ?? [];
  }

  async clearSyncErrors(): Promise<void> {
    await this.db.storage.setMeta(META_ERRORS, []);
    this.set({ rejected: 0 });
  }
}
